import React, { useState, useEffect, useRef } from 'react';
import { MUTED_PALETTE } from './constants';

const GentleDrawing = ({ audioCtxRef, soundEnabled }) => {
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [colorIndex, setColorIndex] = useState(0);
  const [resetKey, setResetKey] = useState(0);
  const [cursorPos, setCursorPos] = useState({ x: 0, y: 0, visible: false });
  const pointsRef = useRef([]);
  
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    canvas.width = canvas.offsetWidth;
    canvas.height = canvas.offsetHeight;
    ctx.fillStyle = '#3D4852';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    pointsRef.current = [];
    
    const animate = () => {
      ctx.fillStyle = 'rgba(61, 72, 82, 0.02)';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      
      pointsRef.current = pointsRef.current.filter(p => p.life > 0);
      pointsRef.current.forEach(p => {
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.size * (p.life / p.maxLife), 0, Math.PI * 2);
        ctx.fillStyle = `${p.color}${Math.floor(p.life / p.maxLife * 255).toString(16).padStart(2, '0')}`;
        ctx.fill();
        p.life -= 1;
        p.x += p.vx;
        p.y += p.vy;
      });
      
      requestAnimationFrame(animate);
    };
    
    requestAnimationFrame(animate);
  }, [resetKey]);
  
  const handlePointerMove = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const x = (e.clientX - rect.left) * (canvas.width / rect.width);
    const y = (e.clientY - rect.top) * (canvas.height / rect.height);
    
    // Update cursor position for the visual circle
    setCursorPos({ x: e.clientX - rect.left, y: e.clientY - rect.top, visible: true });
    
    if (!isDrawing) return;
    
    for (let i = 0; i < 3; i++) {
      pointsRef.current.push({
        x: x + (Math.random() - 0.5) * 20,
        y: y + (Math.random() - 0.5) * 20,
        vx: (Math.random() - 0.5) * 2,
        vy: (Math.random() - 0.5) * 2,
        size: 10 + Math.random() * 10,
        color: MUTED_PALETTE[colorIndex],
        life: 80,
        maxLife: 80,
      });
    }
  };
  
  const handleReset = () => {
    setResetKey(k => k + 1);
  };
  
  return (
    <div className="flex flex-col items-center justify-center h-full gap-5">
      <div className="relative">
        <canvas
          ref={canvasRef}
          className="rounded-xl shadow-2xl"
          style={{ 
            width: '85vw', 
            maxWidth: '900px', 
            height: '55vh', 
            maxHeight: '500px',
            cursor: 'none'
          }}
          onPointerDown={() => setIsDrawing(true)}
          onPointerUp={() => setIsDrawing(false)}
          onPointerLeave={() => { setIsDrawing(false); setCursorPos(p => ({ ...p, visible: false })); }}
          onPointerEnter={() => setCursorPos(p => ({ ...p, visible: true }))}
          onPointerMove={handlePointerMove}
        />
        {/* Circle cursor */}
        {cursorPos.visible && (
          <div
            className="pointer-events-none absolute rounded-full border-2 transition-transform"
            style={{
              width: '32px',
              height: '32px',
              left: cursorPos.x - 16,
              top: cursorPos.y - 16,
              borderColor: MUTED_PALETTE[colorIndex],
              backgroundColor: isDrawing ? `${MUTED_PALETTE[colorIndex]}40` : 'transparent',
              transform: isDrawing ? 'scale(1.2)' : 'scale(1)',
            }}
          />
        )}
      </div>
      <div className="flex gap-3 items-center">
        {MUTED_PALETTE.slice(0, 6).map((color, i) => (
          <button
            key={color}
            onClick={() => setColorIndex(i)}
            className={`w-10 h-10 rounded-full transition-transform shadow-md ${colorIndex === i ? 'scale-125 ring-2 ring-white' : 'hover:scale-110'}`}
            style={{ backgroundColor: color }}
          />
        ))}
        <button
          onClick={handleReset}
          className="ml-4 px-4 py-2 rounded-full bg-white/10 hover:bg-white/20 text-white/70 text-sm transition-all"
        >
          🔄 Clear Canvas
        </button>
      </div>
      <p className="text-white/50 text-sm">Click and drag to draw flowing ribbons</p>
    </div>
  );
};

export default GentleDrawing;
